import { 
    API_ENDPOINTS, 
    customToast, 
    FormInput, 
    Roles, 
    route, 
    SelectOption, 
    Stepper,  
    useAuthorisationContext, 
    useDisabledContext, 
    useLangContext, 
    useQueries, 
    useUserContext,
    Variants
} from "carrier-fe";
import { ToshibaInstallationFormData } from "../../pages/installation/toshiba/journey";
import { UseFormReturn } from "../../types/journey";
import { QUERY_KEYS } from "../../util/Constants";
import { useEffect, useMemo, useState } from "react";
import axios from 'axios';

type SelectKey = 'installer_companies' | 'installer_users';
type Selects = Record<SelectKey, SelectOption[]>;

function ToshibaStep1({
    form,
    loading,
}: {
    form: UseFormReturn<ToshibaInstallationFormData, any>,
    loading?: boolean,
}) {
    const { setValues, store, errors } = form;
    const { crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const { hasRoleAccess } = useAuthorisationContext();
    const { user } = useUserContext();
    const [selects, setSelects] = useState<Selects>({
        installer_companies: [],
        installer_users: [],
    });

    const { 
        data: selectOptions, 
        error, 
        isLoading,
    } = useQueries<{ data: SelectOption[] }>([
        {
            key: QUERY_KEYS.INSTALLER_COMPANIES,
            config: {
                method: 'GET',
                url: route(`${API_ENDPOINTS.GENERIC.SELECT.COMPANIES}?types[]=installer`),
            },
        },
    ]);

    const fetchInstallers = async () => {
        setDisabled(true);

        try {
            const response = await axios.get(
                route(API_ENDPOINTS.GENERIC.SELECT.INSTALLERS, {
                    companyId: store?.installer_company_id,
                })
            );
            setSelects((prev) => ({
                ...prev,
                installer_users: response?.data?.data ?? [],
            }));
        } catch (error: any) {
            customToast({
                title: error?.response?.data?.message || '',
                variant: Variants.Danger,
            });
            setSelects((prev) => ({
                ...prev,
                installer_users: [],
            }));
        } finally {
            setDisabled(false);
        }
    };

    useEffect(() => {
        if (selectOptions) {
            setSelects((prev) => ({
                ...prev,
                installer_companies: selectOptions.installer_companies?.data ?? [],
            }));
        }
        if (error) {
            Object.values(error).map((e) =>
                customToast({ 
                    title: (e?.response?.data as any)?.message, 
                    variant: Variants.Danger 
                })
            );
        }
        if (user?.id && hasRoleAccess(Roles.COMPANY_MANAGER)) {
            setValues({
                installer_company_id: user?.individual?.company_id,
            });
        } else if (user?.id && hasRoleAccess(Roles.ENGINEER)) {
            setValues({
                installer_company_id: user?.individual?.company_id,
                installer_user_id: user?.id,
            });
        } 
    }, [selectOptions, error, user]);

    useEffect(() => {
        if (store?.installer_company_id) {
            fetchInstallers();
        } else {
            setSelects((prev) => ({
                ...prev,
                installer_users: [],
            }));
        }
    }, [store?.installer_company_id]);

    const disableCompanyField = useMemo(() =>
        (hasRoleAccess(Roles.COMPANY_MANAGER) ||
        hasRoleAccess(Roles.ENGINEER)) &&
        !!store?.installer_company_id,

        [store?.installer_company_id]
    );

    const disableEngineerField = useMemo(() => 
        (hasRoleAccess(Roles.ENGINEER) && !!store?.installer_user_id)
        || !store?.installer_company_id, 

        [store?.installer_user_id, store?.installer_company_id]
    );

    return (
        <Stepper.Step
            loading={isLoading || loading}
            title={ crud?.sub_titles?.installer
                || 'Installer'
            }
        >
            <FormInput
                name='installer_company_id'
                type='searchable-select'
                label={crud?.sub_titles?.company
                    || 'Company'
                }
                disabled={disabled || disableCompanyField}
                options={selects.installer_companies}
                value={store?.installer_company_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;

                    setValues({
                        installer_company_id: value,
                    });
                }}
                errorMessages={errors?.installer_company_id}
                clearable={false}
            />

            <FormInput
                name='installer_user_id'
                type='searchable-select'
                label={crud?.models?.engineer
                    || 'Engineer'
                }
                disabled={disabled || disableEngineerField}
                options={selects.installer_users}
                value={store?.installer_user_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;

                    setValues({
                        installer_user_id: value,
                    });
                }}
                errorMessages={errors?.installer_user_id}
                clearable={false}
            />
        </Stepper.Step>
    )
}; 

export default ToshibaStep1;